/**
 * Edge ticket — билетът, с който FE-то се представя пред касата (§2 на
 * договора). Облачният JWT НИКОГА не пътува по LAN: облакът издава ticket
 * (`POST /user/edge-ticket`, TTL 24 h), а FE го праща на всяка заявка към
 * касата в хедъра `X-Edge-Ticket` (WS: query `?ticket=`).
 *
 * Форматът е `<b64url(payload_json)>.<b64url(hmac)>`; FE НЕ проверява
 * подписа, но ДЕКОДИРА payload-а, за да разпознае локално минтнат билет
 * (`"local": true` — издаден от касата при ПИН вход без интернет). Такъв
 * билет служи като `access_token`, докато трае локалният режим; при връщане
 * към облака той се РАЗМЕНЯ за истински токен (`edgeMode.ts` →
 * `POST /user/exchange-edge-ticket`), а провали ли се размяната — смята се за
 * изтекъл и човекът слиза до ПИН екрана.
 *
 * Кешът в `localStorage["edge_ticket"]` пази по един билет ЗА ВСЕКИ облачен
 * токен, под който е издаден (отпечатък = последните 32 знака, както
 * `EmployeeRecord.x` в lib/auth.ts): на общ терминал X (касата) и Y
 * (служителят) имат различни `uid`/права, а ПИН екранът работи с X, докато
 * модулите работят с Y. Издаването (заявката) е в `edgeMode.ts` — този
 * модул е чист, за да го ползва и `api/client.ts` без цикъл.
 */
import {
  EDGE_TICKET_STORAGE_KEY,
  edgeStorageGet,
  edgeStorageRemove,
  edgeStorageSet,
} from "./edgeState";

/** Payload-ът на билета (§2) — само полетата, които FE чете. */
export interface EdgeTicketPayload {
  v: number;
  rid: string;
  uid: number;
  username: string;
  names: string | null;
  personal: boolean;
  iat: number;
  exp: number;
  local: boolean;
}

interface CachedTicket {
  ticket: string;
  /** ISO 8601 UTC от отговора на облака. */
  expiresAt: string;
}

interface TicketCache {
  v: 1;
  tickets: Record<string, CachedTicket>;
}

/** Отпечатък на токена, под който е издаден билетът (виж lib/auth.ts). */
export const tokenFingerprintForTicket = (token: string): string =>
  token.slice(-32);

function decodeBase64Url(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
    // UTF-8 (имената на служителите са на кирилица)
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * Декодира payload-а на edge ticket. JWT (три сегмента, header
 * `{"alg":…}`) и всеки друг низ дават null — така функцията е безопасна
 * върху произволен `access_token`.
 */
export function decodeEdgeTicketPayload(
  ticket: string | null | undefined,
): EdgeTicketPayload | null {
  if (!ticket) return null;
  const segments = ticket.split(".");
  if (segments.length !== 2) return null;
  const json = decodeBase64Url(segments[0]);
  if (!json) return null;
  try {
    const payload = JSON.parse(json) as Partial<EdgeTicketPayload>;
    if (
      !payload ||
      typeof payload !== "object" ||
      payload.v !== 1 ||
      typeof payload.uid !== "number" ||
      typeof payload.exp !== "number"
    ) {
      return null;
    }
    return { ...payload, local: payload.local === true } as EdgeTicketPayload;
  } catch {
    return null;
  }
}

/** Локално минтнат билет (ПИН вход срещу касата без интернет). */
export function isLocalEdgeToken(token: string | null | undefined): boolean {
  return decodeEdgeTicketPayload(token)?.local === true;
}

/**
 * Толерансът, който КАСАТА дава на `exp` (§2 на договора: подпис + `exp`
 * с +5 min + `rid == snapshot.restaurant`). Терминалът дава СЪЩИЯ — иначе
 * сам отказва билет, който касата още приема, и то точно когато часовниците
 * на двете машини се разминават.
 */
const TICKET_CLOCK_TOLERANCE_MS = 300_000;

/**
 * Колко назад след `exp` билетът се държи в кеша, въпреки че вече е изтекъл.
 *
 * Собственият часовник на терминала НЕ Е основание билетът да се ИЗТРИЕ:
 * касов компютър с изтощена батерия и таблет без сверяване скачат с часове, а
 * в аварията няма и интернет, с който да се сверят. Отказът е възстановим
 * (сверява се часът или се минтва нов билет и всичко тръгва), изтриването —
 * не. Затова времето изхвърля само това, което никакъв разумен скок не
 * обяснява: TTL-ът на билета е 24 h, а прагът тук е месец. Целта на чистенето
 * (ключът да не расте с всеки служител, влизал някога на терминала) остава —
 * просто се постига с месец закъснение, а изходът от акаунта и без това трие
 * целия ключ (`clearEdgeTickets`).
 */
const TICKET_DISCARD_AFTER_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Изтекъл ли е локално минтнатият билет. Той Е `access_token`-ът, затова
 * срокът му се чете от самия payload (облачните билети носят `expiresAt` от
 * отговора). Касата го отказва с 401 след `exp` (+5 min толеранс), а 401 от
 * касата НЕ сваля сесията — терминал, чийто локален билет е изтекъл през
 * нощта, би стоял с грешка на всеки екран, вместо да поиска нов вход.
 * Затова изтеклият билет се брои за липсващ: режимът става ОГРАНИЧЕН
 * (§14.3.1) и екранът за вход/ПИН минтва нов срещу касата.
 */
const isExpiredLocalTicket = (
  payload: EdgeTicketPayload,
  now = Date.now(),
): boolean => payload.exp * 1000 + TICKET_CLOCK_TOLERANCE_MS <= now;

function readTicketCache(): TicketCache {
  const raw = edgeStorageGet(EDGE_TICKET_STORAGE_KEY);
  if (!raw) return { v: 1, tickets: {} };
  try {
    const parsed = JSON.parse(raw) as TicketCache;
    if (parsed && parsed.v === 1 && parsed.tickets) return parsed;
  } catch {
    /* повреден запис — започваме на чисто */
  }
  return { v: 1, tickets: {} };
}

/** Изтекъл ЗА ЧЕТЕНЕ — тоест не се праща, но и не се трие (виж по-долу). */
const isExpired = (entry: CachedTicket, now = Date.now()): boolean => {
  const expires = Date.parse(entry.expiresAt);
  return Number.isNaN(expires) || expires + TICKET_CLOCK_TOLERANCE_MS <= now;
};

/**
 * Билет, който САМ обявява друга версия на договора (`{"v": 2, …}`). Само
 * ПОЛОЖИТЕЛНОТО разпознаване брои: низ, който FE-то не може да разчете, НЕ се
 * съди тук — подписа и без това не го проверяваме, а за годността отговаря
 * касата (401). Същото важи за ресторанта (`rid`, §2): терминалът не знае
 * срещу кой обект стои и затова не гадае.
 */
const declaresOtherVersion = (ticket: string): boolean => {
  const segments = ticket.split(".");
  if (segments.length !== 2) return false;
  const json = decodeBase64Url(segments[0]);
  if (!json) return false;
  try {
    const payload = JSON.parse(json) as { v?: unknown } | null;
    return typeof payload?.v === "number" && payload.v !== 1;
  } catch {
    return false;
  }
};

/**
 * Запис, който не носи годен билет по СТРУКТУРА, а не по време: повреден ред,
 * нечетим срок или билет от друга версия на договора. Такъв се изхвърля
 * веднага — часовникът няма нищо общо с него.
 */
const isStructurallyInvalid = (entry: CachedTicket | undefined): boolean =>
  !entry ||
  typeof entry.ticket !== "string" ||
  typeof entry.expiresAt !== "string" ||
  Number.isNaN(Date.parse(entry.expiresAt)) ||
  declaresOtherVersion(entry.ticket);

/** Изтекъл от толкова отдавна, че скок на часовника не го обяснява. */
const isLongExpired = (entry: CachedTicket, now: number): boolean =>
  Date.parse(entry.expiresAt) + TICKET_DISCARD_AFTER_EXPIRY_MS <= now;

function writeTicketCache(cache: TicketCache) {
  // Чисти се при всеки запис, за да не расте ключът с всеки служител, влизал
  // някога на терминала — но часовникът на ТЕРМИНАЛА не трие: скочи ли напред
  // с часове, изхвърленият билет не се връща, приложението влиза в ограничен
  // режим и отказва всичко още преди мрежата.
  const now = Date.now();
  const tickets: Record<string, CachedTicket> = {};
  for (const [fingerprint, entry] of Object.entries(cache.tickets)) {
    if (isStructurallyInvalid(entry)) continue;
    if (isLongExpired(entry, now)) continue;
    tickets[fingerprint] = entry;
  }
  edgeStorageSet(
    EDGE_TICKET_STORAGE_KEY,
    JSON.stringify({ v: 1, tickets } satisfies TicketCache),
  );
}

/** Записва билет, издаден от облака под `issuedForToken`. */
export function storeEdgeTicket(
  issuedForToken: string,
  ticket: string,
  expiresAt: string,
) {
  const cache = readTicketCache();
  cache.tickets[tokenFingerprintForTicket(issuedForToken)] = {
    ticket,
    expiresAt,
  };
  writeTicketCache(cache);
}

/**
 * Билетът, който върви със заявка, тръгнала с `token` като Authorization:
 *  • самият токен, ако е локален билет (ПИН вход срещу касата) и не е
 *    изтекъл — тогава той Е самоличността пред касата;
 *  • иначе кешираният облачен билет за този токен, ако не е изтекъл;
 *  • null — няма с какво да се представим (заявката към касата ще получи 401).
 */
export function getEdgeTicketForToken(
  token: string | null | undefined,
): string | null {
  if (!token) return null;
  const payload = decodeEdgeTicketPayload(token);
  if (payload?.local === true) {
    return isExpiredLocalTicket(payload) ? null : token;
  }
  const entry = readTicketCache().tickets[tokenFingerprintForTicket(token)];
  if (!entry || isExpired(entry)) return null;
  return entry.ticket;
}

/** Има ли валиден ОБЛАЧЕН билет за токена (без да го връща). */
export function hasCloudEdgeTicket(token: string | null | undefined): boolean {
  if (!token || isLocalEdgeToken(token)) return false;
  return getEdgeTicketForToken(token) !== null;
}

export function clearEdgeTickets() {
  edgeStorageRemove(EDGE_TICKET_STORAGE_KEY);
}

// ─── „Билетът беше хвърлен" — следата за екрана за вход ────────────────────
// Връщането на интернета сваля човека, влязъл с ПИН по време на аварията, до
// ПИН екрана (`edgeMode.ts::leaveLocalMode`), когато размяната на билета не е
// минала. Днес това става БЕЗ НИТО ДУМА и изглежда като изгонване без
// причина. Маркерът пренася „защо" през прескачането до ПИН екрана: то е SPA
// пренареждане при служител на общ терминал, но ПЪЛНА навигация при личен
// акаунт — затова `sessionStorage`, а не променлива в модула. Пише се веднъж
// и се КОНСУМИРА от екрана, който го покаже.

const RETURN_TO_PIN_KEY = "edge_return_to_pin";

/** Локалният билет беше хвърлен при връщането в облака (размяната се провали). */
export function markLocalTicketDropped() {
  try {
    sessionStorage.setItem(RETURN_TO_PIN_KEY, "1");
  } catch {
    /* storage недостъпен — човекът просто няма да прочете обяснението */
  }
}

/** Прочита и трие маркера. Второ извикване връща `false`. */
export function consumeLocalTicketDropped(): boolean {
  try {
    if (sessionStorage.getItem(RETURN_TO_PIN_KEY) === null) return false;
    sessionStorage.removeItem(RETURN_TO_PIN_KEY);
    return true;
  } catch {
    return false;
  }
}
